import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TreePine, Utensils, Sparkles, CalendarDays, Gift,
  ArrowRight, Users, Wallet, CheckCircle2, Circle, MinusCircle, Pencil, Globe, Zap, ListChecks, AlertTriangle, Trash2,
} from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { api } from "../lib/api";
import { PlanProgressEditor } from "../components/PlanProgressEditor";
import { FeedbackButton } from "../components/Feedback";

const BLOCK_ICON = { tree: TreePine, utensils: Utensils, sparkles: Sparkles, gift: Gift, calendar: CalendarDays, users: Users };
const TASKS = ["country", "guests", "deco", "ceia", "gifts"];
const BLOCK_TO_BUDGET = { decoration: "decoration", ceia: "ceia", table: "table", mesa: "table", gifts: "gifts" };
const BAND_COLOR = { ok: "#D4AF37", warn: "#F59E0B", over: "#C8102E" };

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const [xmas, setXmas] = useState(undefined); // undefined=loading, null=none
  const [progress, setProgress] = useState(null);
  const [covers, setCovers] = useState([]);
  const [budget, setBudget] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [blocksDone, setBlocksDone] = useState({});
  const [blocksOver, setBlocksOver] = useState({});
  const [blocksOverSave, setBlocksOverSave] = useState({});

  const loadProgress = React.useCallback(() => {
    api.get("/progress").then(({ data }) => setProgress(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/christmas?lang=${lang}`).then(({ data }) => setXmas(data.plan ? data : null)).catch(() => setXmas(null));
    loadProgress();
    api.get(`/christmas/options?lang=${lang}`).then(({ data }) => setCovers(data.covers)).catch(() => {});
    api.get("/budget").then(({ data }) => setBudget(data.budget)).catch(() => {});
    api.get("/plan/blocks-done").then(({ data }) => { setBlocksDone(data.done || {}); setBlocksOver(data.over || {}); setBlocksOverSave(data.over_save || {}); }).catch(() => {});
  }, [lang, loadProgress]);

  const firstName = user?.name?.split(" ")[0] || "";
  const plan = xmas?.plan;
  const c = xmas?.computed;
  const coverImg = covers.find((x) => x.id === plan?.cover)?.img;
  // Live progress: steps auto-check as the user saves in each module; N/A comes from the saved planning editor.
  const naList = progress?.not_applicable || [];
  const liveState = (k) => (naList.includes(k) ? "not_applicable" : progress?.live?.states?.[k] === "completed" ? "completed" : "incomplete");
  const applicable = TASKS.filter((k) => !naList.includes(k));
  const savedPct = applicable.length ? Math.round(applicable.filter((k) => liveState(k) === "completed").length / applicable.length * 100) : 0;
  const fmtWhen = progress?.saved?.saved_at
    ? new Date(progress.saved.saved_at).toLocaleString(lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";

  const openDelete = () => { setDeleteMode(true); setEditorOpen(true); };
  const openEditor = () => {
    setDeleteMode(false);
    setEditorOpen(true);
    api.post("/analytics/event", { event: "christmas_plan_edit_started", meta: {} }).catch(() => {});
  };

  if (xmas === undefined) return <div className="py-20 text-center text-xmas-slate">…</div>;

  // NEW USER — no plan yet
  if (!plan) {
    return (
      <div data-testid="dashboard-empty">
        <div className="float-up mb-8">
          <p className="text-sm uppercase tracking-widest text-xmas-gold">{t("dash.welcome_new")}</p>
          <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">{firstName ? <span className="gold-gradient-text">{firstName}</span> : ""} 🎄</h1>
          <p className="mt-3 text-lg text-xmas-champagne">{t("dash.welcome_new_sub")}</p>
          <FeedbackButton origin="dashboard" className="mt-4" />
        </div>
        <CreateHero t={t} />
        <QuickCard t={t} />
        <DiscoverRow t={t} />
      </div>
    );
  }

  // RETURNING USER — personalized dashboard
  return (
    <div data-testid="dashboard-personalized">
      {/* Meu Natal hero */}
      <div className="float-up relative mb-8 overflow-hidden rounded-3xl border border-xmas-gold/20" data-testid="my-christmas-hero">
        {coverImg && <img src={coverImg} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-xmas-navy via-xmas-navy/80 to-xmas-navy/40" />
        <div className="relative p-6 sm:p-8">
          <p className="text-xs uppercase tracking-widest text-xmas-gold">{t("dash.welcome_back")}{firstName ? `, ${firstName}` : ""}</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-xmas-snow sm:text-4xl" data-testid="plan-name-hero">🎄 {["Meu Natal", "O meu Natal", "My Christmas", "Mi Navidad", "Mon Noël", "Il mio Natale"].includes(plan.name) ? t("plan.title") : plan.name}</h1>
          <p className="mt-2 text-lg text-xmas-champagne" data-testid="days-left">{c.days === 0 ? t("dash.days_today") : t("dash.days_left", { n: c.days })}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-xmas-snow">
            <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-xmas-gold" /> {plan.guests}</span>
            <span className="inline-flex items-center gap-1.5 capitalize"><Sparkles className="h-4 w-4 text-xmas-gold" /> {plan.style}</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-xmas-gold" /> {config?.countries?.find((x) => x.code === plan.country)?.name?.[lang]}</span>
            {plan.budget_total ? <span className="inline-flex items-center gap-1.5"><Wallet className="h-4 w-4 text-xmas-gold" /> {formatMoney(c.symbol, plan.budget_total)}</span> : null}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/app/planner" data-testid="dash-continue" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">{t("dash.continue")} <ArrowRight className="h-4 w-4" /></Link>
            <FeedbackButton origin="dashboard" />
            <button type="button" onClick={openDelete} data-testid="dash-delete-plan" className="inline-flex items-center gap-1.5 rounded-full border border-xmas-crimson/40 px-4 py-2.5 text-sm text-xmas-champagne transition hover:border-xmas-crimson hover:text-xmas-crimson"><Trash2 className="h-4 w-4" /> {t("plan.delete")}</button>
            <Link to="/app/create?edit=1" data-testid="dash-edit" className="inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/30 px-4 py-2.5 text-sm text-xmas-champagne hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /> {t("dash.edit_plan")}</Link>
          </div>
        </div>
      </div>

      {/* What to do now + progress */}
      <div className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6" data-testid="now-card">
          <div className="mb-3 flex items-center gap-2"><Zap className="h-5 w-5 text-xmas-gold" /><h2 className="font-display text-lg text-xmas-snow">{t("dash.now_title")}</h2></div>
          <ul className="space-y-2">
            {c.next_actions.map((a) => (
              <li key={a.id} data-testid={`now-action-${a.id}`} data-done={a.done ? "1" : "0"} className={`flex items-center gap-2 text-sm ${a.done ? "text-xmas-slate line-through decoration-xmas-gold/60" : "text-xmas-champagne"}`}>
                {a.done ? <CheckCircle2 className="h-3.5 w-3.5 text-xmas-gold" /> : <Circle className="h-3.5 w-3.5 text-xmas-gold" />} {a.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="glass rounded-2xl p-6" data-testid="progress-card">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="font-serif-elegant text-lg italic text-xmas-champagne" data-testid="progress-title">{savedPct === 100 ? t("plan.planned_title") : t("dash.taking_shape")}</p>
            <span className="font-mono-num text-xl text-xmas-gold" data-testid="dash-progress-percent">{savedPct}%</span>
          </div>
          <div className="mb-4 h-2 overflow-hidden rounded-full bg-xmas-navysurface"><div className="h-full rounded-full bg-gradient-to-r from-xmas-pine to-xmas-gold transition-all duration-500" style={{ width: `${savedPct}%` }} data-testid="dash-progress-bar" /></div>
          <div className="grid grid-cols-2 gap-1.5">
            {TASKS.map((k) => {
              const st = liveState(k);
              const done = st === "completed";
              const na = st === "not_applicable";
              const Icon = na ? MinusCircle : done ? CheckCircle2 : Circle;
              return <div key={k} data-testid={`progress-task-${k}`} className="flex items-center gap-2 text-sm"><Icon className={`h-4 w-4 ${done ? "text-xmas-gold" : "text-xmas-slate"}`} /><span className={`${done ? "text-xmas-snow" : "text-xmas-slate"} ${na ? "line-through" : ""}`}>{t(`task.${k}`)}</span></div>;
            })}
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/10 pt-3">
            <span className="text-[11px] text-xmas-slate" data-testid="progress-last-update">{progress?.has_saved ? t("plan.last_update", { when: fmtWhen }) : t("plan.not_saved_yet")}</span>
            <button type="button" onClick={openEditor} data-testid="dash-edit-planning" className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-xmas-gold/40 px-4 py-1.5 text-xs font-semibold text-xmas-gold hover:bg-xmas-gold/10"><Pencil className="h-3.5 w-3.5" /> {t("plan.edit")}</button>
          </div>
        </div>
      </div>

      {/* Plan blocks — explainable */}
      <h2 className="mb-4 flex items-center gap-2 font-display text-xl text-xmas-snow"><ListChecks className="h-5 w-5 text-xmas-gold" /> {t("dash.your_plan")}</h2>
      <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {c.blocks.map((b, i) => {
          const Icon = BLOCK_ICON[b.icon] || Sparkles;
          const done = !!blocksDone[b.id];
          const over = Number(blocksOver[b.id] || 0);
          const frame = over > 0
            ? "border-xmas-crimson/60 bg-xmas-navycard/70 shadow-[inset_0_0_0_1px_rgba(200,16,46,0.3)]"
            : done ? "border-[#34D399]/50 bg-xmas-navycard/70 shadow-[inset_0_0_0_1px_rgba(52,211,153,0.2)]" : "border-xmas-gold/15 bg-xmas-navycard/70";
          return (
            <div key={b.id} data-testid={`plan-block-${b.id}`} data-done={done ? "true" : "false"} data-over={over > 0 ? "true" : "false"} className={`card-lift relative flex flex-col rounded-2xl border p-5 ${frame}`} style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`relative flex h-9 w-9 items-center justify-center rounded-full ${done ? "bg-[#34D399]/20" : "bg-xmas-gold/15"}`}>
                    <Icon className={`h-4.5 w-4.5 ${done ? "text-[#34D399]" : "text-xmas-gold"}`} />
                    {done && <CheckCircle2 className="absolute -right-1.5 -top-1.5 h-4.5 w-4.5 rounded-full bg-xmas-navycard text-xmas-gold" data-testid={`plan-block-check-${b.id}`} />}
                  </div>
                  <h3 className="font-display text-base text-xmas-snow">{b.title}</h3>
                </div>
                {b.reserved != null && <span className="rounded-full bg-xmas-gold/10 px-2.5 py-1 text-xs font-semibold text-xmas-gold" data-testid={`plan-reserved-${b.id}`}>{formatMoney(symbolForCurrency(config, b.currency), b.reserved)}</span>}
              </div>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {done && <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[#34D399]/15 px-2 py-0.5 text-[11px] font-semibold text-[#34D399]" data-testid={`plan-block-done-${b.id}`}><CheckCircle2 className="h-3 w-3 text-xmas-gold" /> {t("dash.block_done")}</span>}
                {over > 0 && <Link to={`${b.route}${blocksOverSave[b.id] ? `?save=${encodeURIComponent(blocksOverSave[b.id])}` : ""}`} className="inline-flex w-fit items-center gap-1 rounded-full bg-xmas-crimson/20 px-2 py-0.5 text-[11px] font-semibold text-xmas-crimson transition hover:bg-xmas-crimson/35 hover:underline" data-testid={`plan-block-over-${b.id}`}><AlertTriangle className="h-3 w-3" /> {t("dash.block_over").replace("{v}", formatMoney(symbolForCurrency(config, user?.currency || "BRL"), over))} <ArrowRight className="h-3 w-3" /></Link>}
              </div>
              <p className="mb-3 flex-1 text-xs italic text-xmas-slate">{b.why}</p>
              {(() => {
                const bc = budget?.categories?.[BLOCK_TO_BUDGET[b.id]];
                if (!bc || (bc.limit == null && !bc.planned)) return null;
                return <BudgetMini bc={bc} symbol={symbolForCurrency(config, user?.currency || "BRL")} t={t} testid={b.id} />;
              })()}
              <Link to={b.route} data-testid={`plan-cta-${b.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-xmas-gold hover:underline">{b.cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          );
        })}
      </div>

      <DiscoverRow t={t} />

      <PlanProgressEditor
        open={editorOpen}
        startDelete={deleteMode}
        progress={progress}
        onClose={() => { setEditorOpen(false); setDeleteMode(false); }}
        onSaved={() => { setEditorOpen(false); loadProgress(); }}
        onDeleted={() => { setEditorOpen(false); setXmas(null); setBudget(null); loadProgress(); }}
      />
    </div>
  );
}

function CreateHero({ t }) {
  return (
    <Link to="/app/create" data-testid="create-hero" className="float-up group mb-6 block overflow-hidden rounded-3xl border border-xmas-gold/30 bg-gradient-to-br from-xmas-pine/40 via-xmas-navycard to-xmas-navy p-8 transition hover:border-xmas-gold/60 sm:p-10">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-xmas-gold/15 px-3 py-1 text-xs font-semibold text-xmas-gold"><Sparkles className="h-3.5 w-3.5" /> MY CHRISTMAS</div>
          <h2 className="font-display text-3xl font-bold text-xmas-snow sm:text-4xl">✨ {t("create.cta_title")}</h2>
          <p className="mt-2 max-w-xl text-xmas-champagne">{t("create.cta_sub")}</p>
        </div>
        <span className="btn-gold inline-flex shrink-0 items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wider">{t("create.start")} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
}

function QuickCard({ t }) {
  return (
    <Link to="/app/create?quick=1" data-testid="quick-card" className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-xmas-red/30 bg-xmas-red/10 p-5 transition hover:border-xmas-red/60">
      <div className="flex items-center gap-3">
        <Zap className="h-6 w-6 shrink-0 text-xmas-champagne" />
        <div>
          <h3 className="font-display text-lg text-xmas-snow">⚡ {t("create.quick_title")}</h3>
          <p className="text-sm text-xmas-slate">{t("create.quick_sub")}</p>
        </div>
      </div>
      <ArrowRight className="h-5 w-5 shrink-0 text-xmas-champagne" />
    </Link>
  );
}

function DiscoverRow({ t }) {
  return (
    <div className="mb-4">
      <h2 className="mb-4 font-display text-xl text-xmas-snow">{t("dash.discover")}</h2>
      <Link to="/app/world" data-testid="discover-world" className="card-lift group flex items-center justify-between gap-4 rounded-2xl border border-xmas-gold/15 bg-gradient-to-r from-xmas-navycard to-xmas-navy p-6 transition hover:border-xmas-gold/40">
        <div className="flex items-center gap-4"><span className="text-3xl">🌍</span><div><h3 className="font-display text-lg text-xmas-snow">{t("world.title")}</h3><p className="text-sm text-xmas-slate">{t("world.sub")}</p></div></div>
        <ArrowRight className="h-5 w-5 shrink-0 text-xmas-gold transition group-hover:translate-x-1" />
      </Link>
    </div>
  );
}


function BudgetMini({ bc, symbol, t, testid }) {
  const fmt = (v) => formatMoney(symbol, v || 0);
  const pct = bc.limit ? Math.min(100, Math.round((bc.planned / bc.limit) * 100)) : 0;
  return (
    <div className="mb-3" data-testid={`plan-budget-${testid}`}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-xmas-slate">{bc.limit != null ? t("budget.of", { p: fmt(bc.planned), l: fmt(bc.limit) }) : `${t("budget.planned_amt")}: ${fmt(bc.planned)}`}</span>
        {bc.available != null && (
          <span className={bc.over > 0 ? "text-xmas-crimson" : "text-xmas-champagne"}>
            {bc.over > 0 ? t("budget.band_over", { v: fmt(bc.over) }) : `${fmt(bc.available)} ${t("budget.available2").toLowerCase()}`}
          </span>
        )}
      </div>
      {bc.limit ? (
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-xmas-navysurface">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: BAND_COLOR[bc.band] || "#D4AF37" }} />
        </div>
      ) : null}
    </div>
  );
}
